import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PreVisitQuestion } from '@/api/preVisit/preVisitApi.types';
import { Card } from '@/components/ui/card';
import { PreVisitQuestionField } from '@/components/client/ClientPortal/IntakeForm/preVisitQuestionRenderers';
import type { EditableQuestionRow } from './preVisitFormEditorState';

function toPreviewQuestion(row: EditableQuestionRow): PreVisitQuestion {
  return {
    code: row.code === null ? row.key : row.code,
    questionType: row.questionType,
    labelEn: row.labelEn,
    labelPl: row.labelPl,
    helpEn: row.helpEn.length === 0 ? null : row.helpEn,
    helpPl: row.helpPl.length === 0 ? null : row.helpPl,
    options: row.options.map(function toPreviewOption(option) {
      return {
        code: option.code === null ? option.key : option.code,
        labelEn: option.labelEn,
        labelPl: option.labelPl,
      };
    }),
    isRequired: row.isRequired,
    isActive: true,
    sortOrder: 0,
  };
}

interface PreVisitFormPreviewProps {
  rows: EditableQuestionRow[];
}

export function PreVisitFormPreview({ rows }: PreVisitFormPreviewProps) {
  const { t } = useTranslation('consultations');
  const [previewAnswers, setPreviewAnswers] = useState<Record<string, string | string[]>>({});

  if (rows.length === 0) {
    return (
      <Card padding="md">
        <p className="text-sm text-zoomuted">{t('preVisitEditor.previewEmpty')}</p>
      </Card>
    );
  }

  return (
    <Card padding="md">
      <p className="text-xs font-bold uppercase tracking-wider text-zoomuted mb-3">
        {t('preVisitEditor.previewTitle')}
      </p>
      <div className="space-y-4">
        {rows.map(function renderRow(row) {
          const question = toPreviewQuestion(row);
          return (
            <PreVisitQuestionField
              key={row.key}
              question={question}
              value={previewAnswers[question.code]}
              photos={[]}
              isInvalid={false}
              isUploadingPhoto={false}
              isPhotoActionDisabled
              onChange={function change(value) {
                setPreviewAnswers(function apply(current) {
                  return { ...current, [question.code]: value };
                });
              }}
              onUploadPhoto={function noopUpload() {
                return undefined;
              }}
              onRemovePhoto={function noopRemove() {
                return undefined;
              }}
              onPreviewPhoto={function noopPreview() {
                return undefined;
              }}
            />
          );
        })}
      </div>
    </Card>
  );
}
